import { Injectable } from '@angular/core';

import { MessageEnvelope } from '@cds/push';
import { Page } from '@frontend/vanilla/core';
import { Observable, Subject, finalize, merge, share } from 'rxjs';

import { BaseSubscription, CdsPushService, FixtureAllSubscription, FixtureSlimSubscription } from './cds-push.service';

export interface SubscriptionFixtureLike {
    id: string;
    contexts: string[];
}

export enum SubscriptionKind {
    Full,
    Slim,
}

@Injectable({
    providedIn: 'root',
})
export class CdsPushStoreService {
    private subscriptions: Map<string, Observable<MessageEnvelope>> = new Map<string, Observable<MessageEnvelope>>();

    constructor(
        private cdsPush: CdsPushService,
        private page: Page,
    ) {}

    subscribeToFixture(fixture: SubscriptionFixtureLike, kind: SubscriptionKind = SubscriptionKind.Full): Observable<MessageEnvelope> {
        let subscription = this.subscriptionFactory(fixture, kind, () => {});
        const observables: Observable<MessageEnvelope>[] = [];

        for (const key of subscription.key) {
            if (this.subscriptions.has(key)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                observables.push(this.subscriptions.get(key)!);
            } else {
                const subject = new Subject<MessageEnvelope>();
                subscription = this.subscriptionFactory(fixture, kind, (message) => {
                    subject.next(message);
                });
                this.cdsPush.subscribe(subscription);
                const observable = subject.asObservable().pipe(
                    finalize(() => {
                        this.subscriptions.delete(key);
                        this.cdsPush.unsubscribe(subscription);
                    }),
                    share(),
                );

                this.subscriptions.set(key, observable);
                observables.push(observable);
            }
        }

        if (observables.length === 0) {
            throw new Error('Observables are empty. Subscription Key might be set incorrectly.');
        }

        return merge(...observables);
    }

    private subscriptionFactory(
        fixture: SubscriptionFixtureLike,
        kind: SubscriptionKind,
        callback: (message: MessageEnvelope) => void,
    ): BaseSubscription {
        switch (kind) {
            case SubscriptionKind.Full:
                return new FixtureAllSubscription(fixture.contexts, fixture.id, this.page.lang, callback);
            case SubscriptionKind.Slim:
                return new FixtureSlimSubscription(fixture.contexts, fixture.id, this.page.lang, callback);
        }
    }
}
